import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
});

// Request interceptor to attach token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth Service
export const authService = {
  login: (email, password) =>
    api.post('/auth/login', { email, password }),
  register: (data) =>
    api.post('/auth/register', data),
  getMe: () =>
    api.get('/auth/me'),
};

// User Service
export const userService = {
  getAll: (params) =>
    api.get('/users', { params }),
  getById: (id) =>
    api.get(`/users/${id}`),
  create: (data) =>
    api.post('/users', data),
  update: (id, data) =>
    api.put(`/users/${id}`, data),
  delete: (id) =>
    api.delete(`/users/${id}`),
  bulkUpload: (formData) =>
    api.post('/users/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }),
};

// Department Service
export const departmentService = {
  getAll: (params) =>
    api.get('/departments', { params }),
  create: (data) =>
    api.post('/departments', data),
  update: (id, data) =>
    api.put(`/departments/${id}`, data),
  delete: (id) =>
    api.delete(`/departments/${id}`),
};

// Timesheet Service
export const timesheetService = {
  getAll: (params) =>
    api.get('/timesheets', { params }),
  getById: (id) =>
    api.get(`/timesheets/${id}`),
  create: (data) =>
    api.post('/timesheets', data),
  update: (id, data) =>
    api.put(`/timesheets/${id}`, data),
  submit: (id) =>
    api.put(`/timesheets/${id}/submit`),
  addEntry: (id, data) =>
    api.post(`/timesheets/${id}/entries`, data),
  bulkUpload: (formData) =>
    api.post('/timesheets/bulk-upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60000,
    }),
  updateEntry: (id, entryId, data) =>
    api.put(`/timesheets/${id}/entries/${entryId}`, data),
  deleteEntry: (id, entryId) =>
    api.delete(`/timesheets/${id}/entries/${entryId}`),
};

// Leave Service
export const leaveService = {
  getAll: (params) =>
    api.get('/leaves', { params }),
  getBalance: (userId) =>
    api.get('/leaves/balance', { params: { userId } }),
  create: (data) =>
    api.post('/leaves', data),
  update: (id, data) =>
    api.put(`/leaves/${id}`, data),
  cancel: (id) =>
    api.delete(`/leaves/${id}`),
};

// Leave Type Service
export const leaveTypeService = {
  getAll: (params) =>
    api.get('/leave-types', { params }),
  create: (data) =>
    api.post('/leave-types', data),
  update: (id, data) =>
    api.put(`/leave-types/${id}`, data),
  delete: (id) =>
    api.delete(`/leave-types/${id}`),
};

// Leave Allocation Service
export const leaveAllocationService = {
  getAll: (params) =>
    api.get('/leave-allocations', { params }),
  getForUser: (userId, params) =>
    api.get(`/leave-allocations/user/${userId}`, { params }),
  updateForUser: (userId, data) =>
    api.put(`/leave-allocations/user/${userId}`, data),
  delete: (id) =>
    api.delete(`/leave-allocations/${id}`),
};

// Document Service
export const documentService = {
  getAll: (params) =>
    api.get('/documents', { params }),
  upload: (formData) =>
    api.post('/documents', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  download: (id) =>
    api.get(`/documents/${id}/download`, {
      responseType: 'blob',
    }),
  delete: (id) =>
    api.delete(`/documents/${id}`),
};

// Performance Review Service
export const performanceReviewService = {
  getAll: (params) =>
    api.get('/performance-reviews', { params }),
  getById: (id) =>
    api.get(`/performance-reviews/${id}`),
  create: (data) =>
    api.post('/performance-reviews', data),
  update: (id, data) =>
    api.put(`/performance-reviews/${id}`, data),
  acknowledge: (id) =>
    api.put(`/performance-reviews/${id}/acknowledge`),
};

// Visa Service
export const visaService = {
  getAll: (params) =>
    api.get('/visa', { params }),
  getMy: () =>
    api.get('/visa/my'),
  getById: (id) =>
    api.get(`/visa/${id}`),
  create: (data) =>
    api.post('/visa', data),
  update: (id, data) =>
    api.put(`/visa/${id}`, data),
  getExpiring: (days) =>
    api.get(`/visa/expiring/${days}`),
};

// Notification Service
export const notificationService = {
  getAll: (params) =>
    api.get('/notifications', { params }),
  markRead: (id) =>
    api.put(`/notifications/${id}/read`),
  markAllRead: () =>
    api.put('/notifications/read-all'),
  getUnreadCount: () =>
    api.get('/notifications/unread-count'),
};

// Report Service
export const reportService = {
  getTimesheet: (params) =>
    api.get('/reports/timesheet', { params }),
  getLeave: (params) =>
    api.get('/reports/leave', { params }),
  getVisa: (params) =>
    api.get('/reports/visa', { params }),
  getHeadcount: (params) =>
    api.get('/reports/headcount', { params }),
  getExEmployees: (params) =>
    api.get('/reports/ex-employees', { params }),
};

export default api;
